import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { SiteProfileProvider } from './hooks/useSiteProfile';
import { useHubV2State, type HubV2State } from './hooks/useHubV2State';
import StepProfiel from './steps/StepProfiel';
import StepOpwek from './steps/StepOpwek';
import StepOpslag from './steps/StepOpslag';
import StepGrid from './steps/StepGrid';
import StepResultaten from './steps/StepResultaten';
import LiveResultsPanel from './LiveResultsPanel';
import type { HubSimulation } from '@/hooks/useHubSimulations';

const STEPS = [
  { id: 'profiel', label: 'Profiel', icon: '📊', description: 'Verbruiksprofiel' },
  { id: 'opwek', label: 'Opwek', icon: '☀️', description: 'PV Configuratie' },
  { id: 'opslag', label: 'Opslag', icon: '🔋', description: 'Batterij (BESS)' },
  { id: 'grid', label: 'Grid', icon: '⚡', description: 'Net & Backup' },
  { id: 'resultaten', label: 'Resultaten', icon: '📈', description: 'Analyse & Business Case' },
] as const;

type StepId = typeof STEPS[number]['id'];

export interface HubV2LayoutProps {
  simulation: HubSimulation;
  onSave: () => void;
  onParamsChange: (params: Record<string, any>) => void;
  onFinancialParamsChange: (params: Record<string, any>) => void;
  orgId?: string;
}

function HubV2LayoutInner({ simulation, onParamsChange, onFinancialParamsChange, orgId }: HubV2LayoutProps) {
  const state = useHubV2State(simulation.sim_params, orgId);
  const [activeStep, setActiveStep] = useState<StepId>('profiel');

  // Forward params to parent on changes
  useEffect(() => {
    onParamsChange(state.getParams());
  }, [state.getParams]);

  useEffect(() => {
    onFinancialParamsChange({ financialParams: state.financialParams, deg: state.deg });
  }, [state.financialParams, state.deg]);

  const getStepSummary = (stepId: StepId): string => {
    switch (stepId) {
      case 'profiel': return `${state.resolvedBaseDem.toFixed(0)}${state.resolvedEvDem > 0 ? ` + ${state.resolvedEvDem.toFixed(0)} EV` : ''} MWh/j`;
      case 'opwek': return `${state.pv} MWp`;
      case 'opslag': return `${state.bE} MWh / ${state.bP} MW`;
      case 'grid': return `${state.gMax} MW${state.blokstroomEnabled ? ' + blokstroom' : ''}`;
      case 'resultaten': return `${state.r.ss.toFixed(0)}% zelfvoorziening`;
    }
  };

  const isStepConfigured = (stepId: StepId): boolean => {
    switch (stepId) {
      case 'profiel': return state.resolvedBaseDem > 0;
      case 'opwek': return state.pv > 0;
      case 'opslag': return state.bE > 0;
      case 'grid': return state.gMax > 0;
      case 'resultaten': return true;
    }
  };

  return (
    <div className="flex gap-4 min-h-[calc(100vh-200px)]">
      {/* SIDEBAR */}
      <div className="w-[240px] shrink-0">
        <div className="sticky top-4 space-y-1">
          {STEPS.map((step) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(step.id)}
              className={cn(
                "w-full text-left px-3 py-2.5 rounded-lg border transition-all cursor-pointer",
                activeStep === step.id
                  ? "bg-[#0B1F4A] text-white border-[#0B1F4A] shadow-md"
                  : "bg-white text-[#3D4F6F] border-[#D9E2EC] hover:bg-[#F5F8FB]"
              )}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm">{step.icon}</span>
                <span className="text-xs font-semibold">{step.label}</span>
                {step.id !== 'resultaten' && (
                  <span className="ml-auto text-[10px]">
                    {isStepConfigured(step.id) ? '✅' : '○'}
                  </span>
                )}
              </div>
              {activeStep !== step.id && (
                <div className="text-[9px] mt-0.5 opacity-70 truncate pl-6">
                  {getStepSummary(step.id)}
                </div>
              )}
            </button>
          ))}

          {/* Mini KPIs */}
          <div className="mt-4 p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] space-y-1.5">
            <div className="text-[9px] font-bold text-[#8899AA] uppercase">Live Resultaten</div>
            <div className="text-[10px] space-y-0.5">
              <div className="flex justify-between"><span className="text-[#8899AA]">PV</span><strong>{state.r.pv.toFixed(0)} MWh</strong></div>
              <div className="flex justify-between"><span className="text-[#8899AA]">Vraag</span><strong>{state.r.dem.toFixed(0)} MWh</strong></div>
              <div className="flex justify-between">
                <span className="text-[#8899AA]">Zelf</span>
                <strong className={state.r.ss >= 70 ? 'text-green-600' : 'text-amber-600'}>{state.r.ss.toFixed(1)}%</strong>
              </div>
              <div className="flex justify-between"><span className="text-[#8899AA]">Grid</span><strong>{state.r.gi.toFixed(0)} MWh</strong></div>
              <div className="flex justify-between"><span className="text-[#8899AA]">Backup</span><strong>{state.r.bu.toFixed(0)} MWh</strong></div>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 min-w-0">
        {activeStep === 'profiel' && <StepProfiel state={state} />}
        {activeStep === 'opwek' && <StepOpwek state={state} />}
        {activeStep === 'opslag' && <StepOpslag state={state} />}
        {activeStep === 'grid' && <StepGrid state={state} />}
        {activeStep === 'resultaten' && <StepResultaten state={state} />}

        {activeStep !== 'resultaten' && <LiveResultsPanel state={state} />}
      </div>
    </div>
  );
}

export default function HubV2Layout(props: HubV2LayoutProps) {
  return (
    <SiteProfileProvider>
      <HubV2LayoutInner {...props} />
    </SiteProfileProvider>
  );
}
